// apps/hyperseguros-web/src/components/Nav/NavBar.tsx

'use client'

import React, { forwardRef } from 'react'
import Link from 'next/link'
import { Box, Stack, useTheme } from '@mui/material'
import FocusTrap from '@mui/material/Unstable_TrapFocus'
import { UIButton } from 'renaissance/components/UIButton'
import { glassMaterialCard } from 'renaissance/components/Theme/glass'
import { ctaAccentSx } from './ctaAccent'
import { assetUrl } from '../../lib/images'
import MenuTrigger, { type TMenuTriggerState } from './MenuTrigger'
import type { TNavItem, TNavConfig } from './types'

export const MOBILE_PANEL_ID = 'nav-mobile-panel'

/** Gap between the floating pill and the viewport edges. */
const TOP_OFFSET = 12
/** The pill's own padding above and below its rows. */
const PILL_PADDING = 3

type NavBarProps = {
  config: TNavConfig
  openId: string | null
  mobileOpen: boolean
  /** Mobile: how many screens are pushed over the root list. */
  mobileDepth: number
  isCoarsePointer: boolean
  isDesktop: boolean
  onMenuTriggerEnter: (id: string) => void
  onMenuTriggerFocus: (id: string) => void
  onMenuTriggerClick: (id: string) => void
  onLinkHover: () => void
  onHamburgerClick: () => void
  onBackClick: () => void
  onPanelPointerEnter: () => void
  onPanelPointerLeave: () => void
  onPanelTransitionEnd: (
    e: React.TransitionEvent<globalThis.HTMLElement>
  ) => void
  panelContent: React.ReactNode | null
  panelLabel: string | null
  isPanelOpen: boolean
  barHeight: number
  /** Measured visible height in px; `null` until mounted (see Nav). */
  viewportHeight: number | null
}

const NavBar = forwardRef<globalThis.HTMLElement, NavBarProps>(function NavBar(
  {
    config,
    openId,
    mobileOpen,
    mobileDepth,
    isCoarsePointer,
    isDesktop,
    onMenuTriggerEnter,
    onMenuTriggerFocus,
    onMenuTriggerClick,
    onLinkHover,
    onHamburgerClick,
    onBackClick,
    onPanelPointerEnter,
    onPanelPointerLeave,
    onPanelTransitionEnd,
    panelContent,
    panelLabel,
    isPanelOpen,
    barHeight,
    viewportHeight,
  },
  ref
) {
  const theme = useTheme()
  const dark = theme.palette.mode === 'dark'
  const glass = glassMaterialCard(theme.palette.mode)
  const logoLabel = config.logoLabel ?? 'Hyperseguros'
  const logoHref = config.logoHref ?? '/'
  const logoSrc = dark ? '/hyper_logo_dark.svg' : '/hyper_logo_light.svg'
  const panelId = isDesktop
    ? panelLabel
      ? `nav-panel-${panelLabel}`
      : undefined
    : MOBILE_PANEL_ID

  // Smooth ease-out-expo for the morph. Settles gently at the end so the
  // grow/shrink doesn't feel rushed at the edges.
  const morphEase = 'cubic-bezier(0.16, 1, 0.3, 1)'
  const morphDuration = '420ms'

  // One frosted material for both surfaces. The full-screen mobile card
  // sits over whole pages, so while open it raises the tint for contrast and
  // blurs harder, so whatever sits behind it melts into colour fields.
  const restingBackground = dark
    ? 'rgba(30,30,30,0.45)'
    : 'rgba(255,255,255,0.4)'
  // The phone pill spans a dense slice of the hero, so at rest it needs a
  // whiter tint and a touch more blur than the desktop bar to keep the
  // same contrast; it stays frosted, not opaque.
  const restingBackgroundMobile = dark
    ? 'rgba(30,30,30,0.62)'
    : 'rgba(255,255,255,0.62)'
  const expandedBackground = dark
    ? 'rgba(30,30,30,0.78)'
    : 'rgba(255,255,255,0.78)'
  const background = mobileOpen
    ? expandedBackground
    : { xs: restingBackgroundMobile, md: restingBackground }
  const restingBlur = 'blur(6px) saturate(180%)'
  const restingBlurMobile = 'blur(12px) saturate(180%)'
  const expandedBlur = 'blur(20px) saturate(180%)'
  const blur = mobileOpen
    ? expandedBlur
    : { xs: restingBlurMobile, md: restingBlur }

  // The mobile card fills the visible viewport minus the pill's margins; the
  // panel row needs that explicit height so the 0fr → 1fr track can
  // interpolate. The height is measured (Nav's useVisualViewportHeight)
  // rather than taken from `100dvh`, which iOS Safari evaluates against the
  // wrong toolbar state right after a scroll. `100svh` is only the SSR
  // fallback: the smallest viewport can never overflow the screen.
  const chrome = TOP_OFFSET * 2 + PILL_PADDING * 2 + barHeight
  const mobilePanelHeight =
    viewportHeight === null
      ? `calc(100svh - ${chrome}px)`
      : `${Math.max(0, viewportHeight - chrome)}px`

  // Hamburger at rest, X on the root list, back chevron on any pushed
  // screen — one control, three shapes.
  const triggerState: TMenuTriggerState = !mobileOpen
    ? 'menu'
    : mobileDepth > 0
      ? 'back'
      : 'close'

  return (
    <Box
      component="nav"
      aria-label="Principal"
      ref={ref}
      sx={{
        position: 'fixed',
        top: `${TOP_OFFSET}px`,
        // While the mobile card is open the nav also pins to the bottom edge,
        // so the card can never be laid out past the visible viewport
        // whatever the toolbar does; the card keeps its own height and just
        // has this box as a ceiling.
        bottom: mobileOpen ? `${TOP_OFFSET}px` : 'auto',
        left: 0,
        right: 0,
        zIndex: theme.zIndex.appBar,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        px: { xs: '16px', md: '24px' },
        pointerEvents: 'none',
      }}
    >
      <FocusTrap open={mobileOpen} disableAutoFocus>
        <Box
          // FocusTrap's root must be focusable; -1 keeps it out of the tab order.
          tabIndex={-1}
          onMouseEnter={
            isDesktop && isPanelOpen ? onPanelPointerEnter : undefined
          }
          onMouseLeave={
            isDesktop && isPanelOpen ? onPanelPointerLeave : undefined
          }
          onTransitionEnd={onPanelTransitionEnd}
          sx={{
            ...glass,
            backgroundColor: background,
            backdropFilter: blur,
            WebkitBackdropFilter: blur,
            // The container morphs: pill → rounded card.
            borderRadius: isPanelOpen ? '24px' : '100px',
            padding: `${PILL_PADDING}px`,
            paddingLeft: { xs: '10px', md: '20px' },
            paddingRight: { xs: '10px', md: '20px' },
            maxWidth: '1024px',
            width: '100%',
            maxHeight: mobileOpen ? '100%' : 'none',
            // `clip`, not `hidden`: a hidden box is still a scroll container
            // that the browser may scroll to reveal focused content, which is
            // how the bar row disappeared on iOS. A clipped box cannot scroll.
            overflow: 'clip',
            pointerEvents: 'auto',
            // Two-row grid: bar (auto-height) + panel (animates 0fr → 1fr).
            display: 'grid',
            gridTemplateRows: isPanelOpen ? 'auto 1fr' : 'auto 0fr',
            transition: `grid-template-rows ${morphDuration} ${morphEase}, border-radius ${morphDuration} ${morphEase}, backdrop-filter ${morphDuration} ${morphEase}, background-color ${morphDuration} ${morphEase}`,
            '@media (prefers-reduced-motion: reduce)': {
              transition: 'none',
            },
          }}
        >
          {/* Bar row */}
          <Box
            sx={{
              display: isDesktop ? 'flex' : 'grid',
              gridTemplateColumns: '44px 1fr 44px',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: { xs: 1, md: 4 },
              height: `${barHeight}px`,
              px: { xs: 0, md: 0 },
            }}
          >
            {isDesktop ? (
              <>
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'flex-start',
                  }}
                >
                  <Logo href={logoHref} src={logoSrc} alt={logoLabel} />
                </Box>
                <Stack
                  direction="row"
                  spacing={4}
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {config.items
                    .filter((item) => item.type !== 'cta')
                    .map((item) => (
                      <NavBarItem
                        key={itemKey(item)}
                        item={item}
                        isOpen={item.type === 'menu' && openId === item.label}
                        isCoarsePointer={isCoarsePointer}
                        onMenuTriggerEnter={onMenuTriggerEnter}
                        onMenuTriggerFocus={onMenuTriggerFocus}
                        onMenuTriggerClick={onMenuTriggerClick}
                        onLinkHover={onLinkHover}
                      />
                    ))}
                </Stack>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    flex: 1,
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  {config.items
                    .filter((item) => item.type === 'cta')
                    .map((item) => (
                      <NavBarItem
                        key={itemKey(item)}
                        item={item}
                        isOpen={false}
                        isCoarsePointer={isCoarsePointer}
                        onMenuTriggerEnter={onMenuTriggerEnter}
                        onMenuTriggerFocus={onMenuTriggerFocus}
                        onMenuTriggerClick={onMenuTriggerClick}
                        onLinkHover={onLinkHover}
                      />
                    ))}
                </Stack>
              </>
            ) : (
              <>
                {/* Trigger left, logo centred, an empty cell balancing the trigger. */}
                <MenuTrigger
                  state={triggerState}
                  label={
                    triggerState === 'back'
                      ? config.labels.back
                      : triggerState === 'close'
                        ? config.labels.closeMenu
                        : config.labels.openMenu
                  }
                  controlsId={MOBILE_PANEL_ID}
                  onClick={
                    triggerState === 'back' ? onBackClick : onHamburgerClick
                  }
                />
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Logo href={logoHref} src={logoSrc} alt={logoLabel} />
                </Box>
                <Box aria-hidden="true" />
              </>
            )}
          </Box>

          {/* Panel row — animates 0fr → 1fr on the parent grid */}
          <Box
            id={panelId}
            role="region"
            aria-label={panelLabel ?? undefined}
            aria-hidden={!isPanelOpen}
            inert={isPanelOpen ? undefined : true}
            sx={{
              minHeight: 0,
              overflow: 'clip',
            }}
          >
            <Box
              sx={
                isDesktop
                  ? {
                      opacity: isPanelOpen ? 1 : 0,
                      // Fade in slightly behind the container morph; fade out
                      // faster so the panel never lingers past its slot.
                      transition: isPanelOpen
                        ? `opacity 260ms ${morphEase} 120ms`
                        : `opacity 140ms ${morphEase}`,
                      '@media (prefers-reduced-motion: reduce)': {
                        transition: 'none',
                      },
                    }
                  : {
                      // MobileMenu fills this box with its own scrolling
                      // views; it only fixes the card's height.
                      position: 'relative',
                      height: mobilePanelHeight,
                      overflow: 'clip',
                    }
              }
            >
              {panelContent}
            </Box>
          </Box>
        </Box>
      </FocusTrap>
    </Box>
  )
})

export default NavBar

// The mark's own viewBox is 0 0 512 102 (both hyper_logo_{light,dark}.svg);
// sized here so the browser can reserve layout space before the SVG loads.
const LOGO_HEIGHT = 24
const LOGO_WIDTH = Math.round((LOGO_HEIGHT * 512) / 102)

function Logo({ href, src, alt }: { href: string; src: string; alt: string }) {
  return (
    <Link
      href={href}
      style={{
        textDecoration: 'none',
        color: 'inherit',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      <Box
        component="img"
        src={assetUrl(src)}
        alt={alt}
        width={LOGO_WIDTH}
        height={LOGO_HEIGHT}
        sx={{
          height: '24px',
          width: 'auto',
          display: 'block',
        }}
      />
    </Link>
  )
}

function itemKey(item: TNavItem): string {
  return `${item.type}-${item.label}`
}

type NavBarItemProps = {
  item: TNavItem
  isOpen: boolean
  isCoarsePointer: boolean
  onMenuTriggerEnter: (id: string) => void
  onMenuTriggerFocus: (id: string) => void
  onMenuTriggerClick: (id: string) => void
  onLinkHover: () => void
}

function NavBarItem({
  item,
  isOpen,
  isCoarsePointer,
  onMenuTriggerEnter,
  onMenuTriggerFocus,
  onMenuTriggerClick,
  onLinkHover,
}: NavBarItemProps) {
  const theme = useTheme()
  if (item.type === 'link') {
    return (
      <Link
        href={item.href}
        onMouseEnter={isCoarsePointer ? undefined : onLinkHover}
        style={{
          textDecoration: 'none',
          color: 'inherit',
          fontSize: '14px',
        }}
      >
        {item.label}
      </Link>
    )
  }

  if (item.type === 'cta') {
    return (
      <Link href={item.href} style={{ textDecoration: 'none' }}>
        <Box
          sx={
            item.accent
              ? ctaAccentSx(item.accent, theme.palette.mode)
              : undefined
          }
        >
          <UIButton
            label={item.label}
            variant={item.buttonVariant ?? 'contained'}
            color="primary"
            onClick={() => {}}
          />
        </Box>
      </Link>
    )
  }

  // menu
  const panelId = `nav-panel-${item.label}`
  return (
    <Box
      component="button"
      type="button"
      aria-haspopup="true"
      aria-expanded={isOpen}
      aria-controls={panelId}
      onMouseEnter={
        isCoarsePointer ? undefined : () => onMenuTriggerEnter(item.label)
      }
      // No onMouseLeave on the trigger itself — we want moving from the
      // trigger into the panel (both inside the same pill container) to
      // keep the menu open. Close is scheduled by the pill's onMouseLeave
      // when the cursor truly leaves the whole nav surface.
      onFocus={() => onMenuTriggerFocus(item.label)}
      onClick={() => onMenuTriggerClick(item.label)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') {
          e.preventDefault()
          onMenuTriggerClick(item.label)
        }
      }}
      sx={{
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: 'inherit',
        fontSize: '14px',
        fontFamily: 'inherit',
        padding: '4px 0',
      }}
    >
      {item.label}
    </Box>
  )
}
